import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ErrorMessage, FormikProvider, useFormik } from 'formik'
import { sumBy } from 'lodash'
import { AutoComplete } from 'primereact/autocomplete'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import * as yup from 'yup'
import {
  addMinimizedEmailWindow,
  closeMinimizedEmailWindow,
} from '../../../../../features/projectManagement/projectManagementSlice'
import { useEmailConfig } from '../../../../../hooks/useEmailConfig'
import useToastr from '../../../../../hooks/useToastr'
import { useUnsavedChangesWarning } from '../../../../../hooks/useUnsavedChangesWarning'
import useUserSettingsByEmails from '../../../../../hooks/useUserSettingsByEmails'
import { E2EUserPaginatedModel } from '../../../../../shared/models/E2EUserPaginatedModel'
import { MailModel, MailRecipientModel } from '../../../../../shared/models/Mail.model'
import { UserUploadedFileModel } from '../../../../../shared/models/service-models/UserUploadedFile.model'
import { getByteSize } from '../../../../../utils/helpers/string.helpers'
import { Nullable } from '../../../../../utils/helpers/ts.helpers'
import UsersAutoComplete from '../../../../certificate-list-view/cetificate-list/add-new-certificate/add-new-cert-details/dynamic-field-multiple/users-auto-complete/UsersAutoComplete'
import HandlerType from '../../enums/handler-type'
import PmrE2EProjectDetailsOrderlineModel from '../../interfaces/pmr-e2e-project-details-orderline-model'
import AttachmentsField from './attachments-field/AttachmentsField'
import EmailEditorField from './email-editor-field/EmailEditorField'
import styles from './HandlerAssignmentNotificationEmailEditorDialog.module.scss'
import NotificationDisabledDialog from './notification-disabled-dialog/NotificationDisabledDialog'
import OrderLinesDialog from './orderlines-dialog/OrderLinesDialog'
import UnsavedChangesWarningDialog from './unsaved-changes-warning-dialog/UnsavedChangesWarningDialog'
import { useSaveDraft } from './useSaveDraft'
import { useDiscardDraft } from './useDiscardDraft'
import { MAX_SUBJECT_LENGTH } from './useHandlerAssignmentNotificationEmailEditor'

const mailRecipientValidationSchema = yup.object().shape({
  email: yup.string().email('Invalid email address').required('Email is required'),
})

type Props = {
  visible: boolean
  onHide: (savedDraftId: Nullable<string>, isFromSaveAndClose?: boolean) => void
  onSend: (mail: MailModel) => Promise<void>
  onMinimize: () => void
  selectedNewHandler: Nullable<E2EUserPaginatedModel>
  handlerType: HandlerType
  orderlines: PmrE2EProjectDetailsOrderlineModel[]
  isSending?: boolean
}

export type HandlerAssignmentNotificationEmailEditorDialogRef = {
  setInitialValues: (initialValues: Partial<MailModel>) => void
}

const buildInitialValues = (): MailModel => ({
  to: [],
  cc: [],
  subject: '',
  body: '',
  attachments: [],
})

const HandlerAssignmentNotificationEmailEditorDialog = forwardRef<
  HandlerAssignmentNotificationEmailEditorDialogRef,
  Props
>((props, ref) => {
  const dispatch = useDispatch()
  const { showError } = useToastr()
  const [isSavingAndClosing, setIsSavingAndClosing] = useState<boolean>(false)
  const [isMinimizing, setIsMinimizing] = useState<boolean>(false)
  const [unsavedChangedWarningDialogVisible, setUnsavedChangedWarningDialogVisible] =
    useState<boolean>(false)
  const emailConfig = useEmailConfig()
  const maximumHandlerAssignmentEmailCC = emailConfig?.maximumHandlerAssignmentEmailCC || 5
  const validationSchema = yup.object().shape({
    id: yup.string().optional(),
    to: yup
      .array()
      .of(mailRecipientValidationSchema)
      .min(1, 'At least one recipient is required')
      .required(),
    cc: yup
      .array()
      .of(mailRecipientValidationSchema)
      .max(
        maximumHandlerAssignmentEmailCC,
        `A maximum of ${maximumHandlerAssignmentEmailCC} CC recipients is allowed`
      )
      .required(),
    attachments: yup.array().of(
      yup.object().shape({
        id: yup.string().required('File ID is required'),
      })
    ),
    subject: yup
      .string()
      .required('Subject field is required')
      .max(MAX_SUBJECT_LENGTH, `Subject cannot exceed ${MAX_SUBJECT_LENGTH} characters`),
    body: yup.string().required('Email body is required'),
  })
  const maxTotalSize = emailConfig?.maxHandlerAssignmentEmailSize || 30 * 1024 * 1024 // 30 MB
  const maxTotalSizeMB = Math.floor(maxTotalSize / (1024 * 1024))
  const formik = useFormik<MailModel>({
    initialValues: buildInitialValues(),
    validationSchema,
    onSubmit: handleSend,
  })
  const { values, setFieldValue, setFieldTouched, resetForm } = formik
  useUnsavedChangesWarning(formik.dirty && props.visible)

  useImperativeHandle(
    ref,
    () => ({
      setInitialValues: initialValues => {
        const baseValues = buildInitialValues()

        resetForm({
          values: {
            ...baseValues,
            ...initialValues,
            id: initialValues.id ?? baseValues.id,
            to: initialValues.to ?? baseValues.to,
            cc: initialValues.cc ?? baseValues.cc,
            attachments: initialValues.attachments ?? baseValues.attachments,
          },
        })

        if (editorRef?.current) {
          editorRef.current.editor.setData(initialValues.body ?? '')
        }
      },
    }),
    [resetForm]
  )

  const editorRef = useRef<any>(null)
  useEffect(() => {
    if (props.visible && !!editorRef?.current) {
      editorRef.current.editor.setData(values.body)
    }
  }, [props.visible, editorRef?.current])

  const e2eProjectLineIds = useMemo(() => props.orderlines.map(ol => ol.id), [props.orderlines])
  const { manuallySaveDraft, isManuallySaving, lastSavedDraft } = useSaveDraft({
    draft: values,
    e2eProjectLineIds,
    autoSave: props.visible && formik.dirty && !props.isSending,
    newHandlerInformation: {
      ...props.selectedNewHandler!,
      handlerType: props.handlerType,
    },
  })
  const existingDraftId = props.orderlines[0]?.handlerAssignmentDraft?.id
  const draftId = lastSavedDraft?.id || existingDraftId

  const isDialogDisabled = isManuallySaving || props.isSending
  const { removeDraftFromStore } = useDiscardDraft()

  const [orderlinesDialogVisible, setOrderlinesDialogVisible] = useState<boolean>(false)
  const [notificationsDisabledDialogVisible, setNotificationsDisabledDialogVisible] =
    useState<boolean>(false)

  const { getUserSettings, data: userSettings } = useUserSettingsByEmails(
    [...formik.values.to.map(t => t.email), ...formik.values.cc.map(c => c.email)],
    props.visible
  )
  const hasTurnedOffEmailNotification = userSettings?.some(
    setting => setting.gmawbHandlerAssignmentEmailNotification === false
  )

  if (!props.selectedNewHandler) {
    return null
  }

  const header = 'Send Email to New GMAWB Handler'
  const footer = (
    <div className="text-right">
      <Button
        label="CANCEL"
        className="default-cancel-button"
        onClick={() => setUnsavedChangedWarningDialogVisible(true)}
        disabled={isDialogDisabled}
      />

      <Button
        label="SAVE AND CLOSE"
        className="default-save-button !bg-[#01518a]"
        onClick={async () => {
          try {
            setIsSavingAndClosing(true)
            const manuallySavedDraft = await manuallySaveDraft()
            dispatch(closeMinimizedEmailWindow({ id: manuallySavedDraft!.id }))
            props.onHide(manuallySavedDraft!.id, true)
          } finally {
            setIsSavingAndClosing(false)
          }
        }}
        disabled={isDialogDisabled}
        loading={isSavingAndClosing}
      />

      <Button
        label="SEND"
        className="default-save-button"
        onClick={() => formik.submitForm()}
        disabled={!formik.isValid || isDialogDisabled}
        loading={props.isSending}
      />
    </div>
  )

  const renderErrorMessage = (fieldName: string) => {
    return (
      <ErrorMessage name={fieldName}>
        {errorMessage => (
          <>
            <div></div>
            <div className="text-red-600 italic font-medium -mt-3">{errorMessage}</div>
          </>
        )}
      </ErrorMessage>
    )
  }

  function handleSend(values: MailModel) {
    if (!isValidSize(values.attachments)) {
      showError(
        'Email Content is Too Large',
        `The total size of the email content and attachments exceeds the maximum allowed size of ${maxTotalSizeMB} MB. Please reduce the size of the email content or attachments to proceed.`
      )
      return
    }

    const everyoneHasTurnedOffEmailNotification =
      userSettings?.length > 0 &&
      userSettings?.every(setting => setting.gmawbHandlerAssignmentEmailNotification === false)
    if (everyoneHasTurnedOffEmailNotification) {
      setNotificationsDisabledDialogVisible(true)
      return
    }

    send()
  }

  const renderWarningIcon = (className?: string) => (
    <FontAwesomeIcon
      icon={faExclamationCircle}
      className={classNames('text-[.8rem]', className)}
      color="#ffa700"
    />
  )

  const selectedEmailTemplate = ({ email }: MailRecipientModel) => {
    const isEmailNotificationDisabled =
      getUserSettings(email)?.gmawbHandlerAssignmentEmailNotification === false

    return (
      <span
        className={classNames(isEmailNotificationDisabled && styles.emailTurnedOffNotification)}
      >
        {isEmailNotificationDisabled && renderWarningIcon('mr-2')}
        {email}
      </span>
    )
  }

  const isValidSize = (
    newAttachments: Pick<UserUploadedFileModel, 'originalFileName' | 'fileSize'>[]
  ) => {
    const totalSize = sumBy(newAttachments, file => file.fileSize) + getByteSize(values.body || '')
    return totalSize <= maxTotalSize
  }

  const closeEditor = () => {
    setUnsavedChangedWarningDialogVisible(false)
    dispatch(closeMinimizedEmailWindow({ id: draftId }))
    props.onHide(draftId)
  }

  const minimizeEditor = async () => {
    setIsMinimizing(true)
    const newDraft = await manuallySaveDraft()
    props.onMinimize()
    dispatch(
      addMinimizedEmailWindow({
        id: newDraft.id,
        createdAt: new Date(),
        subject: values.subject,
      })
    )
    setIsMinimizing(false)
  }

  const send = async () => {
    dispatch(closeMinimizedEmailWindow({ id: draftId }))
    await props.onSend(values)
    if (existingDraftId) removeDraftFromStore(existingDraftId)
    if (lastSavedDraft?.id) removeDraftFromStore(lastSavedDraft.id)
  }

  return (
    <>
      <Dialog
        appendTo={document.body}
        visible={props.visible}
        onHide={() => setUnsavedChangedWarningDialogVisible(true)}
        header={header}
        footer={footer}
        className="default"
        contentClassName="!overflow-y-auto"
        maximized
        icons={() => {
          return (
            <button
              className="p-dialog-header-icon p-link !-mr-1"
              onClick={minimizeEditor}
              disabled={isDialogDisabled || isMinimizing}
            >
              <span className={isMinimizing ? 'pi pi-spin pi-spinner' : 'pi pi-minus'} />
            </button>
          )
        }}
      >
        <FormikProvider value={formik}>
          <div className="font-medium">
            <div className="grid grid-cols-[auto_1fr] gap-4 items-center">
              {hasTurnedOffEmailNotification && (
                <>
                  <div></div>
                  <div className="text-[#ffa700] text-[.9rem] italic -mb-2 font-semibold">
                    Recipients displaying a{' '}
                    <div className="inline-flex items-center gap-[.15rem] not-italic mx-1">
                      {"'"}
                      {renderWarningIcon()}
                      {"'"}
                    </div>
                    have disabled their GMAWB email notifications and will not be notified of this
                    email. Kindly reach out to the intended recipient directly.
                  </div>
                </>
              )}

              <div>To</div>
              <div>
                <AutoComplete
                  disabled
                  className={classNames(
                    'w-full [&_.p-autocomplete-multiple-container]:w-full',
                    styles.emailField
                  )}
                  multiple
                  value={values.to}
                  selectedItemTemplate={selectedEmailTemplate}
                />
              </div>

              <div>CC</div>
              <div>
                <UsersAutoComplete
                  className={classNames(
                    'w-full [&_.p-autocomplete-multiple-container]:w-full [&_.p-autocomplete-multiple-container]:gap-y-2',
                    styles.emailField
                  )}
                  value={values.cc}
                  field="email"
                  onChange={e => {
                    const newValue = e.value as (string | E2EUserPaginatedModel)[]
                    setFieldValue(
                      'cc',
                      newValue.map(v =>
                        typeof v === 'string'
                          ? { email: v }
                          : { email: v.normalizedEmail || (v as any).email }
                      )
                    )
                  }}
                  multiple
                  valueType="object"
                  autoClearInputOnBlur
                  autoFitOptionsWidth={false}
                  minSearchInputLength={1}
                  onBlur={() => setFieldTouched('cc')}
                  selectedItemTemplate={selectedEmailTemplate}
                  source="gmawb-users"
                  disabled={isDialogDisabled}
                />
              </div>
              {renderErrorMessage('cc')}

              <div>Subject</div>
              <div>
                <InputText
                  className="w-full"
                  value={values.subject}
                  onChange={e => setFieldValue('subject', e.target.value)}
                  onBlur={() => setFieldTouched('subject')}
                  disabled={isDialogDisabled}
                />
              </div>
              {renderErrorMessage('subject')}

              <div>Attachments</div>
              <div>
                <AttachmentsField
                  maxTotalSizeMB={maxTotalSizeMB}
                  isValidSize={isValidSize}
                  disabled={isManuallySaving}
                />
              </div>
              {renderErrorMessage('attachments')}
            </div>

            <div className="mt-5">
              <div className="mb-2">
                Order lines included in this reassignment:
                <button
                  className="text-blue-500 hover:underline italic bg-transparent border-none ml-1 p-0 font-semibold"
                  onClick={() => setOrderlinesDialogVisible(true)}
                >
                  Show
                </button>
              </div>

              <EmailEditorField
                ref={editorRef}
                maxTotalSizeMB={maxTotalSizeMB}
                isValidSize={isValidSize}
                disabled={isManuallySaving}
              />
            </div>
          </div>
        </FormikProvider>
      </Dialog>

      <OrderLinesDialog
        visible={orderlinesDialogVisible}
        onHide={() => setOrderlinesDialogVisible(false)}
        orderlines={props.orderlines}
      />

      <NotificationDisabledDialog
        visible={notificationsDisabledDialogVisible}
        onHide={() => setNotificationsDisabledDialogVisible(false)}
        onProceed={() => {
          setNotificationsDisabledDialogVisible(false)
          send()
        }}
      />

      <UnsavedChangesWarningDialog
        onConfirm={closeEditor}
        onHide={() => setUnsavedChangedWarningDialogVisible(false)}
        visible={unsavedChangedWarningDialogVisible}
      />
    </>
  )
})

export default HandlerAssignmentNotificationEmailEditorDialog
